import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Tooltip,
  Badge,
  Popconfirm
} from 'antd';
import {
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { overduePaymentAPI, OverduePayment, OverduePaymentFilters, OverduePaymentStats } from '../../services/overduePaymentAPI';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const OverduePaymentsList: React.FC = () => {
  const [overduePayments, setOverduePayments] = useState<OverduePayment[]>([]);
  const [stats, setStats] = useState<OverduePaymentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<OverduePaymentFilters>({});
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<OverduePayment | null>(null);
  const [paymentForm] = Form.useForm();

  useEffect(() => {
    loadOverduePayments();
    loadStatistics();
  }, [filters]);

  const loadOverduePayments = async () => {
    setLoading(true);
    try {
      const response = await overduePaymentAPI.getOverduePayments(filters);
      if (response.success && response.data) {
        setOverduePayments(response.data);
      } else {
        message.error(response.message || 'Gecikmiş ödənişlər yüklənmədi');
      }
    } catch (error) {
      message.error('Gecikmiş ödənişlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await overduePaymentAPI.getOverdueStatistics(filters);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Statistikalar yüklənmədi:', error);
    }
  };

  const handleMarkAsPaid = (payment: OverduePayment) => {
    setSelectedPayment(payment);
    setPaymentModalVisible(true);
    paymentForm.setFieldsValue({
      amount: parseFloat(payment.remaining_amount),
      payment_method: 'nəğd',
      payment_date: dayjs()
    });
  };

  const handlePaymentSubmit = async (values: any) => {
    if (!selectedPayment) return;

    try {
      const response = await overduePaymentAPI.markAsPaid(
        selectedPayment.id,
        values.amount,
        values.payment_method,
        values.payment_date.format('YYYY-MM-DD')
      );

      if (response.success) {
        message.success(response.message);
        setPaymentModalVisible(false);
        loadOverduePayments();
        loadStatistics();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error('Ödəniş tamamlanmadı');
    }
  };

  const getPriorityColor = (daysOverdue: number) => {
    if (daysOverdue >= 60) return 'red';
    if (daysOverdue >= 30) return 'orange';
    if (daysOverdue >= 15) return 'yellow';
    return 'green';
  };

  const getPriorityLabel = (daysOverdue: number) => {
    if (daysOverdue >= 60) return 'Kritik';
    if (daysOverdue >= 30) return 'Yüksək';
    if (daysOverdue >= 15) return 'Orta';
    return 'Aşağı';
  };

  const columns = [
    {
      title: 'Tələbə',
      key: 'student',
      render: (record: OverduePayment) => (
        <div>
          <div className="font-medium">{record.student_name}</div>
          <div className="text-xs text-gray-500">
            <Space size="small">
              <span><UserOutlined /> {record.student_fin}</span>
              <span>{record.company_name}</span>
            </Space>
          </div>
        </div>
      ),
    },
    {
      title: 'Kurs',
      dataIndex: 'course_name',
      key: 'course_name',
    },
    {
      title: 'Dövrü',
      key: 'period',
      render: (record: OverduePayment) => (
        <div>
          <div className="font-medium">{record.period_display}</div>
          <div className="text-xs text-gray-500">
            Son tarix: {dayjs(record.due_date).format('DD.MM.YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Məbləğ',
      key: 'amount',
      render: (record: OverduePayment) => (
        <div>
          <div className="font-medium text-red-600">
            {parseFloat(record.remaining_amount).toFixed(2)} AZN
          </div>
          <div className="text-xs text-gray-500">
            Tələb: {parseFloat(record.required_amount).toFixed(2)} AZN
          </div>
          {parseFloat(record.paid_amount) > 0 && (
            <div className="text-xs text-green-600">
              Ödənilib: {parseFloat(record.paid_amount).toFixed(2)} AZN
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Gecikən Günlər',
      key: 'days_overdue',
      render: (record: OverduePayment) => (
        <div className="text-center">
          <Badge
            count={record.days_overdue}
            style={{
              backgroundColor: getPriorityColor(record.days_overdue) === 'red' ? '#ff4d4f' :
                             getPriorityColor(record.days_overdue) === 'orange' ? '#fa8c16' :
                             getPriorityColor(record.days_overdue) === 'yellow' ? '#fadb14' : '#52c41a'
            }}
          />
          <div className="text-xs mt-1">
            <Tag color={getPriorityColor(record.days_overdue)}>
              {getPriorityLabel(record.days_overdue)}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: OverduePayment) => (
        <Tag 
          color={record.status === 'gecikir' ? 'red' : 'orange'} 
          icon={<WarningOutlined />}
        >
          {record.status === 'gecikir' ? 'Gecikir' : 
           record.status === 'gözləyir' ? 'Gözləyir' : 
           'Tam ödənilməyib'}
        </Tag>
      ),
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (record: OverduePayment) => (
        <Space size="small">
          <Tooltip title="Ödənişi tamamla">
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleMarkAsPaid(record)}
            >
              Ödə
            </Button>
          </Tooltip>
          
          
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="mb-0">
          <ExclamationCircleOutlined className="mr-2" />
          Gecikmiş Ödənişlər
        </Title>
      </div>

      {/* Statistikalar */}
      {stats && (
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Ümumi Gecikmiş"
                value={stats.total_overdue}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Ümumi Məbləğ"
                value={parseFloat(stats.total_amount)}
                precision={2}
                suffix="AZN"
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Kritik Hallar"
                value={stats.critical_count}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Yüksək Prioritet"
                value={stats.high_count}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          {stats.filter_date && (
            <Col xs={24}>
              
            </Col>
          )}
        </Row>
      )}

      {/* Filterlər */}
      <Card className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="Tələbə adı ilə axtarış..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <DatePicker
              placeholder="Tarix seçin"
              value={filters.filter_date ? dayjs(filters.filter_date) : null}
              onChange={(date) => setFilters({ 
                ...filters, 
                filter_date: date ? date.format('YYYY-MM-DD') : undefined 
              })}
              format="DD.MM.YYYY"
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <InputNumber
                placeholder="Min gün"
                value={filters.days_overdue_min}
                onChange={(value) => setFilters({ ...filters, days_overdue_min: value || undefined })}
                min={0}
              />
              <InputNumber
                placeholder="Max gün"
                value={filters.days_overdue_max}
                onChange={(value) => setFilters({ ...filters, days_overdue_max: value || undefined })}
                min={0}
              />
            </Space>
          </Col>
        </Row>
        <div className="mt-4 text-sm text-gray-500">
          <CalendarOutlined className="mr-1" />
          Tarix seçməklə həmin tarixə görə gecikmiş ödənişləri görə bilərsiniz. 
          Boş buraxsanız bugünkü tarixə görə göstəriləcək.
        </div>
      </Card>

      {/* Gecikmiş ödənişlər cədvəli */}
      <Card>
        <Table
          columns={columns}
          dataSource={overduePayments}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} gecikmiş ödəniş`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Ödəniş tamamlama modalı */}
      <Modal
        title="Ödənişi Tamamla"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={500}
      >
        {selectedPayment && (
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <div><strong>Tələbə:</strong> {selectedPayment.student_name}</div>
            <div><strong>Kurs:</strong> {selectedPayment.course_name}</div>
            <div><strong>Dövrü:</strong> {selectedPayment.period_display}</div>
            <div><strong>Qalan məbləğ:</strong> {selectedPayment.remaining_amount} AZN</div>
            <div><strong>Gecikən günlər:</strong> {selectedPayment.days_overdue} gün</div>
          </div>
        )}
        
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePaymentSubmit}
        >
          <Form.Item
            name="amount"
            label="Ödəniş Məbləği"
            rules={[{ required: true, message: 'Məbləğ daxil edin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              addonAfter="AZN"
            />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label="Ödəniş Üsulu"
            rules={[{ required: true, message: 'Ödəniş üsulu seçin' }]}
          >
            <Select placeholder="Ödəniş üsulu seçin">
              <Option value="nəğd">Nəğd</Option>
              <Option value="kart">Bank kartı</Option>
              <Option value="köçürmə">Bank köçürməsi</Option>
              <Option value="online">Online ödəniş</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="payment_date"
            label="Ödəniş Tarixi"
            rules={[{ required: true, message: 'Ödəniş tarixi seçin' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setPaymentModalVisible(false)}>
              Ləğv et
            </Button>
            <Button type="primary" htmlType="submit">
              Ödənişi Tamamla
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OverduePaymentsList;